import { useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useAuthGate } from "@/lib/authGate"
import { useUpgrade } from "../pro/ProButton"
import type { PlanChoice } from "@/features/billing/services/razorpayCheckout"
import { FALLBACK_ADDON_MINOR, addonFor, planOf, priceMinor, rupees, useAddons, usePlans } from "@/features/billing/plan"
import { PlanCard, type PlanLine } from "../ds/PlanCard"
import { APP_URL, AppLink, CONTACT, SiteLayout } from "./SiteLayout"

/** The four tiers: Free, Nira Plus, Nira Pro, Nira for firms. Prices come from the plan list, with these as the fallback. */
const FREE: PlanLine[] = [
  { text: "10 research questions a day in your first month" },
  { text: "Then 5 questions a month" },
  { text: "One full notice analysis, with the officer's reading" },
  { text: "One Appeal or not reading" },
  { text: "One department's reading" },
  { text: "10 document uploads" },
]
const PLUS: PlanLine[] = [
  { lead: "40", text: "questions a month" },
  { text: "Everything in Free" },
]
const proLines = (draft: string, edits: string): PlanLine[] => [
  { lead: "10", text: "research messages a day" },
  { lead: "5", text: "notice analyses a day" },
  { lead: "3", text: "Appeal or not checks a day" },
  { lead: "10", text: "re-runs with documents a day" },
  { text: "The department's reading on every question" },
  { lead: "1", text: "order draft a month, with 10 edits" },
  { text: `Extra draft ${draft}, 5 more edits ${edits}`, quiet: true },
]
const FIRMS: PlanLine[] = [
  { text: "Everything in Nira Pro" },
  { text: "Seats and shared workspaces across partners" },
  { text: "The workbench: every client's notices, orders and deadlines in one place" },
  { text: "Your data held under your control: dedicated storage, retention, an audit log" },
  { text: "Exposure intelligence connected to your books: GSTR-2B, registers, ledgers, the exposure watch" },
]

/** What a paywall never stands in front of (HANDOFF pricing doc): it only ever asks on a new action, before the model runs. */
const NEVER = "The time-limit result, citations, both sides and what Nira cannot see, share links, client views, print and PDF, reminders, on-device masking, the sample order draft, and anything already on your screen."

const FAQ = [
  { q: "What is “the department's side”?", a: "How an adjudicating officer would argue your matter, written the way an order would put it, followed by your answer to it in order. It is the part of a reply that takes the longest to get right." },
  { q: "What happens to what I upload?", a: "It is read for the reading you asked for, and for nothing else. It is never used to train a model, ours or anyone else's, and there is no setting that turns that on. Delete the workspace and it goes from your account at once; copies are erased from our systems within 30 days." },
  { q: "Is this legal advice?", a: "No. It is research, with every claim linked to its source. Your CA or advocate decides what to do with it." },
]

/** Start a plan, in place: the paywall opens over this page (mounted by NiraFullScreen), or checkout does. A guest signs
 * in first and comes back to /pricing?upgrade=1 (&plan=plus for Plus), which starts it. On the standalone site (APP_URL set)
 * the modal is not mounted, so it stays a link into the app. */
function StartPlan({ plan, label }: { plan: PlanChoice; label: string }) {
  const { signedIn, loaded, requireLogin } = useAuthGate()
  const { go, busy } = useUpgrade("pricing", plan === "plus" ? "nira.message" : "nira.officer_reading", false, "direct", plan)
  const { search, pathname } = useLocation()
  const navigate = useNavigate()
  const q = new URLSearchParams(search)
  const back = plan === "plus" ? "/pricing?upgrade=1&plan=plus" : "/pricing?upgrade=1"
  const resume = !APP_URL && signedIn && q.get("upgrade") === "1" && (q.get("plan") ?? "pro") === plan
  useEffect(() => {
    if (!resume) return
    navigate(pathname, { replace: true })
    void go()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume])
  if (APP_URL) return <AppLink className="nst-btn primary nst-tier-cta" to={`/ask?upgrade=1${plan === "plus" ? "&plan=plus" : ""}`}>{label}</AppLink>
  return (
    <button type="button" className="nst-btn primary nst-tier-cta" disabled={busy || !loaded}
      onClick={() => (signedIn ? void go() : requireLogin("login_required", back))}>
      {label}
    </button>
  )
}

const perMonth = (price: string) => <div className="nst-price per">{price}<span>/month</span></div>

/** Pricing (Site-Pricing): the answer is free, the work product is paid. */
export default function SitePricing() {
  const { signedIn } = useAuthGate()
  const plans = usePlans()
  const addons = useAddons()
  const plus = rupees(priceMinor(planOf(plans, "plus"), "plus"))
  const pro = rupees(priceMinor(planOf(plans, "pro"), "pro"))
  const draft = rupees(addonFor(addons, "order.draft")?.price_minor ?? FALLBACK_ADDON_MINOR.extra_draft)
  const edits = rupees(addonFor(addons, "order.draft_edit")?.price_minor ?? FALLBACK_ADDON_MINOR.extra_edits)
  return (
    <SiteLayout title="Pricing">
      <section className="nst-hero slim">
        <div className="nst-wrap nst-price-head">
          <h1>Research is free. The work product is paid.</h1>
          <p className="nst-lede">Ask up to 10 research questions a day in your first month, then 5 a month. Reading, forwarding and printing are never charged for, and the first notice is read in full. People who reply to notices for a living read every one the way the officer will, in Nira Pro.</p>
        </div>
      </section>

      <section className="nst-tiers-sec">
        <div className="nst-wrap nst-tiers">
          <PlanCard eyebrow="For anyone" name="Free" price={<div className="nst-price">₹0</div>} lines={FREE}
            action={<AppLink className="nst-btn nst-tier-cta" to="/ask">Ask a question</AppLink>}
            note={signedIn ? "No credit card needed. You're signed in: your workspaces are kept." : "No credit card needed. Ask your first question now, no account needed. Sign in to keep your workspaces."} />
          <PlanCard eyebrow="For regular questions" name="Nira Plus" lines={PLUS} price={perMonth(plus)}
            action={<StartPlan plan="plus" label="Start Plus" />}
            note="Cancel any time. GST included." />
          <PlanCard tone="featured" eyebrow="For practitioners" name="Nira Pro" lines={proLines(draft, edits)} price={perMonth(pro)}
            action={<StartPlan plan="pro" label="Start Pro" />}
            note="Cancel any time. GST invoice for your firm." />
          <PlanCard tone="paid" eyebrow="For a firm" name="Nira for firms" lines={FIRMS}
            price={<div className="nst-price ask">By entities</div>}
            action={<a className="nst-btn nst-tier-cta on-navy" href={`${CONTACT}%20for%20firms`}>Talk to us</a>}
            note="Set up with you, for your firm's clients." />
        </div>
      </section>

      <section className="nst-never-sec">
        <div className="nst-wrap">
          <p className="nst-never"><b>Never charged for:</b> {NEVER}</p>
        </div>
      </section>

      <section className="nst-faq-sec">
        <div className="nst-wrap nst-faq">
          <p className="nst-k">Questions</p>
          {FAQ.map((f, i) => (
            <div className={`nst-q${i === FAQ.length - 1 ? " last" : ""}`} key={f.q}><h3>{f.q}</h3><p>{f.a}</p></div>
          ))}
        </div>
      </section>
    </SiteLayout>
  )
}
